// src/pages/CheckoutPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useApi } from "../utils/Api";
import ErrorModal from "../components/ErrorModal";

import SpinnerOverlay from "../components/SpinnerOverlay";
import ServiceTypeSelector from "../components/Checkout/ServiceTypeSelector";
import GuestDetailsForm from "../components/Checkout/GuestDetailsForm";
import OrderSummary from "../components/Checkout/OrderSummary";

const DELIVERY_DRAFT_KEY = "delivery_draft_v1";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { apiFetch } = useApi();
  const { cartItems, loading: cartLoading } = useCart();

  const [serviceType, setServiceType] = useState("pick_up");
  const [isDineIn, setIsDineIn] = useState(false);
  const [tableNumber, setTableNumber] = useState("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [notes, setNotes] = useState("");

  const [deliveryAddress, setDeliveryAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    postcode: "", // ❌ never persisted
  });

  const [deliveryQuote, setDeliveryQuote] = useState({
    status: "idle", // idle | loading | ok | not_deliverable | error
    fee: 0,
    distanceMiles: null,
    quoteId: null,
    message: "",
  });

  const [checkingStock, setCheckingStock] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDeliveryNotice, setShowDeliveryNotice] = useState(false);

  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");

  const showError = (msg) => {
    setErrorMessage(msg || "Something went wrong.");
    setShowErrorModal(true);
  };

  const isAdvanceDelivery = useMemo(() => {
    const ukFormatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "numeric",
      hour12: false,
    });
    const currentUKHour = parseInt(ukFormatter.format(new Date()), 10);
    return currentUKHour < 15 || currentUKHour >= 21;
  }, []);

  const handleServiceTypeSelect = (type) => {
    if (type === "delivery" && isAdvanceDelivery) {
      setShowDeliveryNotice(true);
    }
    setServiceType(type);
  };

  // -------------------------
  // Delivery draft helpers
  // -------------------------
  const loadDeliveryDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(DELIVERY_DRAFT_KEY);
      if (!raw) return { line1: "", line2: "", city: "" };
      const parsed = JSON.parse(raw);
      return {
        line1: typeof parsed?.line1 === "string" ? parsed.line1 : "",
        line2: typeof parsed?.line2 === "string" ? parsed.line2 : "",
        city: typeof parsed?.city === "string" ? parsed.city : "",
      };
    } catch {
      return { line1: "", line2: "", city: "" };
    }
  }, []);

  const saveDeliveryDraft = useCallback((draft) => {
    try {
      const safe = {
        line1: draft?.line1 ?? "",
        line2: draft?.line2 ?? "",
        city: draft?.city ?? "",
      };
      localStorage.setItem(DELIVERY_DRAFT_KEY, JSON.stringify(safe));
    } catch {
      // ignore storage errors
    }
  }, []);

  // ✅ one setter to update fields + persist (except postcode)
  const updateDeliveryField = useCallback(
    (field, value) => {
      setDeliveryAddress((prev) => {
        const next = { ...prev, [field]: value };

        // Persist ONLY non-postcode fields
        if (field !== "postcode") {
          saveDeliveryDraft({
            line1: next.line1,
            line2: next.line2,
            city: next.city,
          });
        }

        return next;
      });
    },
    [saveDeliveryDraft],
  );

  // -------------------------
  // Existing logic
  // -------------------------
  useEffect(() => {
    if (cartLoading) return;
    if (cartItems.length === 0) navigate("/menu", { replace: true });
  }, [cartLoading, cartItems.length, navigate]);

  useEffect(() => setNotes(""), [serviceType]);

  useEffect(() => {
    const savedFullName = localStorage.getItem("guest_fullName") || "";
    const savedEmail = localStorage.getItem("guest_email") || "";
    const savedPhone = localStorage.getItem("guest_phone") || "";
    setFullName(savedFullName);
    setEmail(savedEmail);
    setPhone(savedPhone);
  }, []);

  useEffect(() => {
    if (serviceType !== "eat_in") setTableNumber("");
  }, [serviceType]);

  useEffect(() => {
    if (serviceType !== "pick_up") {
      setPickupDate("");
      setPickupTime("");
    }
  }, [serviceType]);

  // ✅ When serviceType changes:
  // - ALWAYS reset postcode (forces re-enter + fresh quote)
  // - reset quote
  // - if delivery selected, restore last saved line1/line2/city (but keep postcode empty)
  useEffect(() => {
    // reset quote every time option changes (safe)
    setDeliveryQuote({
      status: "idle",
      fee: 0,
      distanceMiles: null,
      quoteId: null,
      message: "",
    });

    setDeliveryAddress((prev) => {
      const draft = loadDeliveryDraft();

      if (serviceType === "delivery") {
        return {
          ...prev,
          line1: draft.line1,
          line2: draft.line2,
          city: draft.city,
          postcode: "", // ✅ always cleared
        };
      }

      // not delivery => still clear postcode
      return { ...prev, postcode: "" };
    });
  }, [serviceType, loadDeliveryDraft]);

  // ✅ Delivery check after typing (debounced)
  useEffect(() => {
    if (serviceType !== "delivery") return;

    const postcode = (deliveryAddress.postcode || "").trim();
    const line1 = (deliveryAddress.line1 || "").trim();
    const city = (deliveryAddress.city || "").trim();

    if (!postcode || postcode.length < 5 || !line1 || !city) {
      setDeliveryQuote({
        status: "idle",
        fee: 0,
        distanceMiles: null,
        quoteId: null,
        message: "",
      });
      return;
    }

    const timer = setTimeout(async () => {
      setDeliveryQuote({
        status: "loading",
        fee: 0,
        distanceMiles: null,
        quoteId: null,
        message: "",
      });

      try {
        const data = await apiFetch("delivery-quote", {
          postcode,
          line1,
          city,
        });

        if (data?.success && data?.deliverable) {
          const quoteId = data?.quote_id || null;

          setDeliveryQuote({
            status: quoteId ? "ok" : "error",
            fee: Number(data.delivery_fee || 0),
            distanceMiles:
              data?.distance_miles != null ? Number(data.distance_miles) : null,
            quoteId,
            message: quoteId
              ? ""
              : "Delivery quote missing. Please re-enter postcode.",
          });

          if (!quoteId) {
            showError("Delivery quote missing. Please re-enter your postcode.");
          }
        } else {
          const msg = data?.message || "We don’t deliver to this location.";
          setDeliveryQuote({
            status: "not_deliverable",
            fee: 0,
            distanceMiles:
              data?.distance_miles != null ? Number(data.distance_miles) : null,
            quoteId: null,
            message: msg,
          });
          showError(msg);
        }
      } catch (err) {
        setDeliveryQuote({
          status: "error",
          fee: 0,
          distanceMiles: null,
          quoteId: null,
          message: "Could not check delivery right now.",
        });
        showError("Could not check delivery right now. Please try again.");
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [
    deliveryAddress.postcode,
    deliveryAddress.line1,
    deliveryAddress.city,
    serviceType,
    apiFetch,
  ]);

  const totalPrice = useMemo(
    () =>
      cartItems
        .reduce((sum, item) => sum + (item.totalPrice || 0), 0)
        .toFixed(2),
    [cartItems],
  );

  const checkStockBeforePlaceOrder = async () => {
    try {
      setCheckingStock(true);
      const data = await apiFetch("check-stock", { cartItems });

      if (!data || data.success !== true) return true;

      if (data.soldOutItems?.length > 0) {
        const names = data.soldOutItems.map((i) => i.item_name);
        showError(
          `The following items are sold out: ${names.join(
            ", ",
          )}. Please remove them from your basket to proceed.`,
        );
        return false;
      }

      return true;
    } catch {
      return true;
    } finally {
      setCheckingStock(false);
    }
  };

  const buildCheckoutPayload = () => {
    const cleanNotes = notes.trim();

    return {
      order_type:
        serviceType === "pick_up"
          ? (isDineIn ? "Eat In" : "Pick Up")
          : "Delivery",

      table_number: null,
      notes: cleanNotes ? cleanNotes : null,

      customer_name: fullName.trim(),
      customer_email: email.trim(),
      customer_phone: phone.trim(),

      pickup_date: serviceType === "pick_up" ? pickupDate : null,
      pickup_time: serviceType === "pick_up" ? pickupTime : null,

      delivery_address:
        serviceType === "delivery"
          ? {
              line1: deliveryAddress.line1.trim(),
              line2: deliveryAddress.line2.trim() || null,
              city: deliveryAddress.city.trim(),
              postcode: deliveryAddress.postcode.trim(),
            }
          : null,

      delivery_quote_id:
        serviceType === "delivery" && deliveryQuote.status === "ok"
          ? String(deliveryQuote.quoteId || "")
          : "",

      service_charge: 0,
      delivery_fee:
        serviceType === "delivery" && deliveryQuote.status === "ok"
          ? deliveryQuote.fee
          : 0,

      items: cartItems.map((ci) => {
        const base = Number(ci?.item?.price || 0);
        const required_option_id = ci.selectedRequiredOption
          ? Number(ci.selectedRequiredOption.id)
          : 0;
        const required_option_price = ci.selectedRequiredOption
          ? Number(ci.selectedRequiredOption.price || 0)
          : 0;

        const selectedOptionalOptions = (ci.selectedOptionalOptions || []).map(
          (o) => ({
            id: Number(o.id),
            option_price: Number(o.option_price || 0),
            quantity: Number(o.quantity || 1),
          }),
        );

        const swallowOptions = (ci?.item?.swallowOptions || []).map((o) => ({
          id: Number(o.id),
          option_price: Number(o.option_price || 0),
          quantity: Number(o.quantity || 1),
        }));

        const proteins = (ci?.item?.proteins || []).map((o) => ({
          id: Number(o.id),
          option_price: Number(o.option_price || 0),
          quantity: Number(o.quantity || 1),
        }));

        return {
          item_id: Number(ci?.item?.id),
          quantity: Number(ci?.quantity || 1),
          base_price: base,
          required_option_id,
          required_option_price,
          selectedOptionalOptions,
          swallowOptions,
          proteins,
        };
      }),
    };
  };

  const handlePlaceOrder = async () => {
    setShowErrorModal(false);
    setErrorMessage("");

    if (!fullName.trim() || !email.trim() || !phone.trim()) {
      showError("Please enter your name, email, and phone number.");
      return;
    }

    if (serviceType === "pick_up") {
      const typeName = isDineIn ? "dine-in" : "pick up";
      if (!pickupDate) {
        showError(`Please select a ${typeName} date.`);
        return;
      }

      if (!pickupTime) {
        showError(`Please select a ${typeName} time.`);
        return;
      }
    }

    if (serviceType === "delivery") {
      if (!deliveryAddress.line1.trim() || !deliveryAddress.city.trim()) {
        showError("Please enter your delivery address and city.");
        return;
      }

      if (deliveryQuote.status !== "ok" || !deliveryQuote.quoteId) {
        showError(
          deliveryQuote.message || "Please enter a valid delivery postcode.",
        );
        return;
      }
    }

    const ok = await checkStockBeforePlaceOrder();
    if (!ok) return;

    try {
      setPlacingOrder(true);
      const payload = buildCheckoutPayload();
      const data = await apiFetch("create-checkout-session", payload);

      if (
        data?.success === false &&
        /(quote.*expired|expired.*quote)/i.test(data?.message || "")
      ) {
        setDeliveryQuote({
          status: "idle",
          fee: 0,
          distanceMiles: null,
          quoteId: null,
          message: "",
        });

        // ✅ force re-enter postcode
        setDeliveryAddress((p) => ({ ...p, postcode: "" }));

        showError(
          "Delivery quote expired. Please re-enter your postcode to refresh the delivery fee.",
        );
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      showError(data?.message || data?.error || "Could not start payment.");
    } catch (err) {
      showError("Server error. Please try again.");
    } finally {
      setPlacingOrder(false);
    }
  };

  const isLoading = placingOrder || checkingStock;
  
  const disablePay =
    (serviceType === "delivery" &&
      (deliveryQuote.status !== "ok" || !deliveryQuote.quoteId)) ||
    (serviceType === "pick_up" && (!pickupDate || !pickupTime));

  const getOverlayMessage = () => {
    if (placingOrder) return "Processing your order...";
    if (checkingStock) return "Checking availability...";
    if (deliveryQuote.status === "loading") return "Checking delivery address...";
    return "Loading...";
  };

  if (cartLoading) return <SpinnerOverlay message="Loading cart..." />;

  return (
    <>
      {(isLoading || deliveryQuote.status === "loading") && <SpinnerOverlay message={getOverlayMessage()} />}

      {showErrorModal && (
        <ErrorModal
          title="Error"
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />
      )}

      {showDeliveryNotice && (
        <ErrorModal
          title="Notice"
          message="Deliveries are fulfilled from 3:00 PM to 9:00 PM. Your order will be delivered during these hours."
          onClose={() => setShowDeliveryNotice(false)}
          okText="Ok, got it"
        />
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="flex items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
            Checkout
          </h1>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex w-max items-center gap-2 px-3 py-2 sm:px-4 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all active:scale-95"
          >
            <span className="material-icons text-base">arrow_back</span>
            Back
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 space-y-8">
            <ServiceTypeSelector
              serviceType={serviceType}
              setServiceType={handleServiceTypeSelect}
            />

            {serviceType === "delivery" && (
              <div className="bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 p-4 rounded-xl flex gap-3 items-start border border-blue-100 dark:border-blue-800">
                <span className="material-icons mt-0.5">info</span>
                <p className="text-sm font-medium">
                  Deliveries are fulfilled from 3:00 PM to 9:00 PM. Your order will be delivered during these hours.
                </p>
              </div>
            )}

            <GuestDetailsForm
              serviceType={serviceType}
              isDineIn={isDineIn}
              setIsDineIn={setIsDineIn}
              fullName={fullName}
              setFullName={setFullName}
              email={email}
              setEmail={setEmail}
              phone={phone}
              setPhone={setPhone}
              tableNumber={tableNumber}
              setTableNumber={setTableNumber}
              notes={notes}
              setNotes={setNotes}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              deliveryQuote={deliveryQuote}
              updateDeliveryField={updateDeliveryField}
              pickupDate={pickupDate}
              setPickupDate={setPickupDate}
              pickupTime={pickupTime}
              setPickupTime={setPickupTime}
            />
          </div>

          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <OrderSummary
              cartItems={cartItems}
              totalPrice={totalPrice}
              isLoading={isLoading}
              checkingStock={checkingStock}
              placingOrder={placingOrder}
              onPlaceOrder={handlePlaceOrder}
              deliveryFee={
                serviceType === "delivery" && deliveryQuote.status === "ok"
                  ? deliveryQuote.fee
                  : 0
              }
              disablePay={disablePay}
            />
          </div>
        </div>
      </main>
    </>
  );
}
